import React, { SetStateAction, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { logError } from "../../../../service/error";
import { logon } from "../../../../service/identity";
import { acceptTerms } from "../../../../service/organisation";
import { GetCurrentUserResponse } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../utils";
import { getDashboardRoute } from "../../../../utils/routes";
import { Toast } from "../../../../widget";

interface UseLogonFormReturnData {
  origin: string | null;
  email: string;
  password: string;
  termOfServiceProductName: string;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  isAcceptAndContinueLoading: boolean;
  termOfServiceErrors: ServiceError[] | undefined;
  showTermOfServiceModal: boolean;
  setEmail: React.Dispatch<SetStateAction<string>>;
  setPassword: React.Dispatch<SetStateAction<string>>;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleTermOfServiceClose: () => void;
  handleAcceptAndContinue: () => void;
}

export const useLogonForm = (): UseLogonFormReturnData => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setCurrentUser, logoutUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [isAcceptAndContinueLoading, setIsAcceptAndContinueLoading] = useState(false);
  const [termOfServiceErrors, setTermOfServiceErrors] = useState<ServiceError[] | undefined>([]);
  const [termOfServiceProductName, setTermOfServiceProductName] = useState("Hub");
  const [showTermOfServiceModal, setShowTermOfServiceModal] = useState(false);

  const errorCodeNoLinkedOrganisations = "VALIDATION_LOGON_NO_ACTIVE_LINKED_ORGANISATIONS";
  const termsAcceptedVersionRequired = "1.3";

  const hasAcceptedTermsVersion = (currentUserResult: GetCurrentUserResponse): boolean => {
    const acceptedTermsVersion = currentUserResult.organisations[0]?.termsAccepted;

    return acceptedTermsVersion !== null && acceptedTermsVersion === termsAcceptedVersionRequired;
  };

  const getUserOrganisationContext = (
    currentUserResult: GetCurrentUserResponse
  ): {
    dashboardType: OrganisationTypeConstants | null;
    productName: "Hub" | "Seed";
  } => {
    const hasDeveloperUuid = currentUserResult.organisations.some((org) => org.developerUuid !== null);
    const hasVerifierUuid = currentUserResult.organisations.some((org) => org.verifierUuid !== null);
    const hasDevelopmentManagerUuid = currentUserResult.organisations.some(
      (org) => org.developmentManagerUuid !== null
    );
    const hasAssetManagerUuid = currentUserResult.organisations.some((org) => org.assetManagerUuid !== null);

    if (hasDeveloperUuid) {
      return { dashboardType: OrganisationTypeConstants.DEVELOPER, productName: "Hub" };
    }

    if (hasVerifierUuid) {
      return { dashboardType: OrganisationTypeConstants.VERIFIER, productName: "Hub" };
    }

    if (hasDevelopmentManagerUuid) {
      return { dashboardType: OrganisationTypeConstants.DEVELOPMENT_MANAGER, productName: "Hub" };
    }

    if (hasAssetManagerUuid) {
      return { dashboardType: OrganisationTypeConstants.ASSET_MANAGER, productName: "Seed" };
    }

    return { dashboardType: null, productName: "Hub" };
  };

  const navigateUserToDashboard = (currentUserResult: GetCurrentUserResponse): void => {
    const { dashboardType } = getUserOrganisationContext(currentUserResult);

    if (dashboardType) {
      navigate(getDashboardRoute(dashboardType), { replace: true });
      return;
    }

    logError({ error: `Could not navigate user with uuid ${currentUserResult.userUuid} to dashboard` });
    Toast.error({ message: "Could not navigate user to dashboard" });
  };

  const handleTermOfServiceClose = (): void => {
    setShowTermOfServiceModal(false);
    logoutUser();
  };

  const handleAcceptAndContinueRequest = useIsLoadingWrapper(async (): Promise<void> => {
    setTermOfServiceErrors([]);

    const result = await acceptTerms({ termsAccepted: termsAcceptedVersionRequired });
    if (result.status === Status.Error) {
      setTermOfServiceErrors(result.errors);
      return;
    }

    if (!user) {
      Toast.error({ message: "Could not navigate user to dashboard" });
      return;
    }

    setShowTermOfServiceModal(false);
    navigateUserToDashboard(user);
  }, setIsAcceptAndContinueLoading);

  const handleAcceptAndContinue = (): void => {
    handleAcceptAndContinueRequest().catch(() => undefined);
  };

  const handleSubmit = useIsLoadingWrapper(async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);
    setTermOfServiceErrors([]);
    setShowTermOfServiceModal(false);

    const res = await logon({ username: email, password });
    if (res.status === Status.Success && res.data) {
      window.ACCESS_TOKEN = res.data?.accessToken;
      const currentUserResult = await setCurrentUser(res.data?.userUuid);

      if (!currentUserResult?.organisations || currentUserResult.organisations.length === 0)
        setErrors([
          {
            code: errorCodeNoLinkedOrganisations,
            message: getErrorMessageFromCode(errorCodeNoLinkedOrganisations),
            property: "username",
          },
        ]);
      else if (!hasAcceptedTermsVersion(currentUserResult)) {
        setTermOfServiceProductName(getUserOrganisationContext(currentUserResult).productName);
        setShowTermOfServiceModal(true);
      } else if (location.state && (location.state as Location)?.pathname.includes("download-zip")) {
        navigate((location.state as Location)?.pathname, { replace: true });
      } else {
        navigateUserToDashboard(currentUserResult);
      }
    } else {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  return {
    origin: searchParams.get("origin"),
    email,
    password,
    termOfServiceProductName,
    errors,
    isHandleSubmitLoading,
    isAcceptAndContinueLoading,
    termOfServiceErrors,
    showTermOfServiceModal,
    setEmail,
    setPassword,
    handleSubmit,
    handleTermOfServiceClose,
    handleAcceptAndContinue,
  };
};
